
import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';

import { VersionsService, VersionsViewService, VersionsEditorService } from '@app/services/app/versions.service';
import { ProjectsService, ProjectsEditorService } from '@app/services/app/projects.service';

import * as Objects from '@core/objects';
import { AppService } from '@app/services/app/app.service';
import { VersionFeedback, ShareVersionFeedback } from '@core/models/version-feedback';
import * as fromState from '@app/store/state/versions/versions.state';
import { AccessProject, AccessProjectKeys } from '@app/models/access';


import { ApprovalActionTypeEnum, ApprovalActionResponse, ApprovalActionResponseCodeEnum, ApprovalSignatureCredentials, AprovalCfr11PinStatusCodeEnum } from "@core/models/digital-signature.model";
import * as VS  from '@core/models/version-share.model';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';

import { ThreeDotService } from '@core/services/three-dot/three-dot.service'
@Component({
    selector: 'versions-details-content-info',
    templateUrl: './info.component.html',
    styleUrls: ['./info.component.scss']
})
export class VersionsDetailsContentInfoComponent implements OnInit, OnChanges {
    public readonly SIGNATURE_TYPES_ENUM = ApprovalActionTypeEnum;    
    public readonly PIN_STATUS = AprovalCfr11PinStatusCodeEnum;
    public readonly SHARE_TYPE = VS.VersionShareType;

    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_NOT_ALLOWED = 2;

    public readonly VIEW_APPROVAL_NONE = 0;
    public readonly VIEW_APPROVAL_APPROVE_MODAL = 1;
    public readonly VIEW_APPROVAL_REJECT_MODAL = 2;
    public readonly VIEW_APPROVAL_DONE = 3;
    public readonly VIEW_FEEDBACK_ACTION_ERROR = 4; 
    public readonly VIEW_APPROVAL_APPROVE_SIGNATURE = 5;
    public readonly VIEW_APPROVAL_SIGNATURE_ERROR = 6;  
    public readonly VIEW_APPROVAL_SET_PASS_MODAL = 7;
    public readonly VIEW_APPROVAL_ACTION_ERROR = 8; // wrong content
    public readonly VIEW_SHARE_UPDATE_STATUS_FORBIDDEN = 9; 
    public readonly VIEW_SHARE_NOT_FOUND_ERROR = 10; 

    public readonly PLUGIN_FEEDBACK = 'share_feedback';
    
    @Input() version: Objects.Version;
    @Input() feedbacks: Array<VersionFeedback>;
    @Output() feedbackDone  = new EventEmitter<VS.VersionShare>();
    @Output() versionShareStatusUpdated  = new EventEmitter<VS.VersionShare>();

    public status: number = 0;
    public view: number = this.VIEW_APPROVAL_NONE;
    public accessFeedbacks: boolean = false;
    public accessProject: AccessProject;
    public feedback: VersionFeedback = null;
    public userShareVersionFeedback: ShareVersionFeedback = null;
    public userApproval: VS.VersionShare = null;
    public userFeedback: VS.VersionShare = null;
    public userVersionUpload: VS.VersionShare = null;
    public currentVersionShare: VS.UploadVersionShare = null;
    public project: Objects.Request = null;
    public signatureType: ApprovalActionTypeEnum = ApprovalActionTypeEnum.DEFAULT;

    private user: Objects.User = null;
    private approvalMessage: string = '';
    public approvalActionDisabled: boolean = false;
    // public rejectDisabled: boolean = false;
    public signDisabled: boolean = false;
    public approveStatus: boolean;
    public pinRequestStatus: AprovalCfr11PinStatusCodeEnum =  this.PIN_STATUS.DEFAULT;

    public versionSharesList: Array<VS.VersionShare> = null;
    // we need order here for multiple items on menu
    public sharesMenu: Array<ThreeDotsMenuItem> = [];
    protected archiveShareMenu: ThreeDotsMenuItem = {
        title: "Archive",
        key: "archive-share",
        icon: "archive-approval",
        order: 1,
        triggered: false
    };
    protected resetShareMenu: ThreeDotsMenuItem = {
        title: "Reset",
        key: "reset-share", // if change here, change as well in core-version-feedbacks-item
        icon: "reset-share",
        order: 2,
        triggered: false // including may-2024 to display pending actions.
    };

    protected appSubscriptor: Subscription;
    protected accessProjectSubscriptor: Subscription;
    protected projectSubscriptor: Subscription;
    protected archiveActionStatus: Subscription;
    protected resetActionStatus: Subscription;
    protected selectVersionSharesSubscriptor: Subscription;
    protected currentUploadVSSubscriptor: Subscription;
    
    constructor (
        private versionsSrv: VersionsService,
        private versionsViewSrv: VersionsViewService,
        private projectsEditorSrv: ProjectsEditorService,
        public appSrv: AppService,
        private projectsSrv: ProjectsService,
        private dotSrv: ThreeDotService
    ){}

    ngOnInit(): void {

        this.appSubscriptor = this.appSrv.getSession().subscribe( (session: any) => {
            this.accessFeedbacks = session.options.plugins.some((pa) => pa == this.PLUGIN_FEEDBACK);
            this.user = session.current.user;
            // need to check access to load feedbacks
            if(this.accessFeedbacks){
                this.setSharesMenu();

            //     // this.loadUserVersionFeedback(); // not user anymore, now loaded in version store
                this.versionsViewSrv.loadShareFeedback(this.user);
            }
        });

        this.projectSubscriptor = this.projectsSrv.selectProject().subscribe((project: Objects.Request) => {
            this.project = project;
        });

        this.accessProjectSubscriptor = this.projectsEditorSrv.selectAccessAll().subscribe((access: AccessProject)=> {
            this.accessProject = access;
            this.setSharesMenu();
        });

        this.currentUploadVSSubscriptor = this.versionsViewSrv.selectCurrentUploadVersionShare().subscribe((uvShare: VS.UploadVersionShare) => {
            if(uvShare != null){
                this.currentVersionShare = uvShare;
            }
            this.initView();
        })

        // this subs goes according call status of the action
        // use versionActionAtatus to display modals and make all calls in store
        this.archiveActionStatus = this.versionsViewSrv.selectArchiveActionStatus().subscribe((status: fromState.VersionsActionStatus) => {
            if(status.success){
                // this.view = this.VIEW_SHARE_ARCHIVED_MODAL; // not required
                this.dotSrv.setTriggered(status.dispatcher, false);
            }
            if (status.error != null){
                if(status.error.status == 403){
                    this.view = this.VIEW_SHARE_UPDATE_STATUS_FORBIDDEN
                } if(status.error.status == 404){
                    this.view = this.VIEW_SHARE_NOT_FOUND_ERROR;
                }else{
                    this.view = this.VIEW_FEEDBACK_ACTION_ERROR;
                }
                this.dotSrv.setTriggered(status.dispatcher, false);
            }
        })
        this.resetActionStatus = this.versionsViewSrv.selectResetActionStatus().subscribe((status: fromState.VersionsActionStatus) => {
            if(status.success){
                // this.view = this.VIEW_SHARE_RESET_MODAL; // not required
                this.dotSrv.setTriggered(status.dispatcher, false); // 
                // this.setSharesMenu();
            }
            if (status.error != null){
                if(status.error.status == 403){
                    this.view = this.VIEW_SHARE_UPDATE_STATUS_FORBIDDEN;
                } if(status.error.status == 404){
                    this.view = this.VIEW_SHARE_NOT_FOUND_ERROR;
                }else{
                    this.view = this.VIEW_FEEDBACK_ACTION_ERROR;
                }
                this.dotSrv.setTriggered(status.dispatcher, false);
                // this.setSharesMenu();
            }
        } )

        this.selectVersionSharesSubscriptor = this.versionsViewSrv.selectVersionShares().subscribe((allShares:VS.VersionShare[]) => {
            if(allShares != null){
                this.versionSharesList = allShares;
                // separate upload-version and feedbacks
                this.userApproval = allShares.find((vs:VS.VersionShare) => vs.type == this.SHARE_TYPE.APPROVAL_TYPE && vs.entry.user.id == this.user.id);
                this.userFeedback = allShares.find((vs:VS.VersionShare) => vs.type == this.SHARE_TYPE.FEEDBACK_TYPE && vs.entry.user.id == this.user.id);
                // find my last version share
                this.userVersionUpload = allShares
                    .filter((vs: VS.VersionShare) => vs.type === this.SHARE_TYPE.UPLOAD_VERSION_TYPE && vs.entry.user.id === this.user.id)
                    .sort((a, b) => new Date(b.status.date).getTime() - new Date(a.status.date).getTime())
                    .find((upload: VS.VersionShare) => upload.entry?.token !== '');
                if(this.userVersionUpload && this.userVersionUpload.entry?.token != '' ){
                    this.versionsViewSrv.loadUploadVersionShares(this.userVersionUpload.entry.token);
                }
                this.initView();
            }   

        });

        this.dotSrv.resetAll();
        
    }

    ngOnDestroy() {
        this.view = this.VIEW_APPROVAL_NONE;
        this.appSubscriptor.unsubscribe();
        this.projectSubscriptor.unsubscribe();
        this.archiveActionStatus.unsubscribe();
        this.resetActionStatus.unsubscribe();
        this.selectVersionSharesSubscriptor.unsubscribe();
        this.accessProjectSubscriptor.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // if(changes.feedbacks)
        //     this.loadMyFeedback(this.version);
        // TODO: update on shares change   
    }

    initView() {
        if(this.versionSharesList != null && this.version != null )
            this.status = this.STATUS_DONE;
    }

    onApprove(event: any) : void {
        // view approve modal popup
        this.view = this.VIEW_APPROVAL_APPROVE_MODAL;
    } 

    onReject(event: any) : void {
        // view reject modal popup
        this.view = this.VIEW_APPROVAL_REJECT_MODAL;
    }

    onPassRequiredDone():void {
        this.approvalMessage = '';
        this.view = this.VIEW_APPROVAL_NONE;
    }

    signApprove(pass: string):void {
        this.signDisabled = true;
        let credentials: ApprovalSignatureCredentials = { password: pass };
        if(this.userApproval != null){
            this.versionsSrv.giveFeedback$(this.version,this.userApproval.entry.token, this.approvalMessage, this.approveStatus, credentials).subscribe(
                (data:any) => {
                    if( this.userApproval != null)
                        this.feedbackDone.emit(this.userApproval);
                    console.debug("Approved sent "+this.version.id,data);
                    // this.loadMyFeedback(this.version);
                    this.view = this.VIEW_APPROVAL_DONE;
                    this.approvalMessage = '';
                    this.signDisabled = false;
                },
                (error:any) => {
                    if(error.status == 403){
                        this.approvalMessage = '';
                        this.view = this.VIEW_APPROVAL_SIGNATURE_ERROR;
                    }else{
                        this.view = this.VIEW_FEEDBACK_ACTION_ERROR;
                    }
                    this.signDisabled = false;
                    
                }
            );
        }
    }

    public approve(message: string){
        this.approvalActionDisabled = true;
        this.approvalMessage = message;
        this.approveStatus = true;
        if(this.userApproval != null){
            this.approvalFeedbackAction();
        }
    }

    cfr11SignFeedback(credentials: ApprovalSignatureCredentials) {
        this.signDisabled = true;
        // sign action.
        if(credentials != null){
            this.versionsSrv.giveFeedback$(this.version,this.userApproval.entry.token , this.approvalMessage, this.approveStatus, credentials).subscribe(
                (data:any) => {
                    if( this.userApproval != null)
                        this.feedbackDone.emit(this.userApproval);
                    console.debug("Sign approval sent "+this.version.id, data);
                    this.view = this.VIEW_APPROVAL_DONE;
                    this.pinRequestStatus =  this.PIN_STATUS.DEFAULT;
                    this.signDisabled = false;
                },
                (error:any) => {
                    console.debug("Sign approval errorrrr  "+this.version.id, error);
                    if(error.status == 403){
                        this.approvalMessage = '';
                        this.view = this.VIEW_APPROVAL_SIGNATURE_ERROR;
                    }else{
                        this.view = this.VIEW_FEEDBACK_ACTION_ERROR;
                    }
                    this.approvalActionDisabled = false;
                    this.signDisabled = false;
                    this.pinRequestStatus =  this.PIN_STATUS.DEFAULT;
                }
            );
        }
    }

    public reject(message: any){
        this.approvalActionDisabled = true;
        this.approvalMessage = message;
        this.approveStatus = false;
        if(this.userApproval != null){
            this.approvalFeedbackAction();
        }
    }

    public complete(){
        this.view = this.VIEW_APPROVAL_NONE;
        if(this.userFeedback != null){
            this.versionsSrv.completeFeedback$(this.version,this.userFeedback.entry.token).subscribe({
                next:(data:any) => {
                    if( this.userFeedback != null)
                        this.feedbackDone.emit(this.userFeedback);
                    console.debug("Complete action sent "+this.version.id,data);
                    // this.loadMyFeedback(this.version);
                    this.view = this.VIEW_APPROVAL_DONE;
                },
                error: (error:any) => {
                    this.view = this.VIEW_FEEDBACK_ACTION_ERROR;
                }
            });
        }
    }

    /**
     * @deprecated since ending 2023 due to new seignature procedure
     * @param provider 
     */
    onSsoSign(provider: string){
        // ACTION TO APPROVE WITH SIGNATURE BY SSO NOT ON CFR11
        this.signDisabled = true;
        if(this.userApproval != null){
            this.versionsSrv.signFeedback$(this.version,this.userApproval.entry.token, this.approvalMessage, this.approveStatus, provider).subscribe(
                (data:any) => {
                    this.feedbackDone.emit();
                    console.debug("Approved sent "+this.version.id,data);
                    // this.loadMyFeedback(this.version);
                    this.view = this.VIEW_APPROVAL_DONE;
                    this.approvalMessage = '';
                    this.signDisabled = false;
                },
                (error:any) => {
                    if(error.status == 403){
                        this.approvalMessage = '';
                        this.view = this.VIEW_APPROVAL_SIGNATURE_ERROR;
                    }else{
                        this.view = this.VIEW_FEEDBACK_ACTION_ERROR;
                    }
                    this.signDisabled = false;
                    
                }
            );
        }
    }

    onNewPinRequest(e:any) {
        //  Resend approve call for send a new pin email
        this.pinRequestStatus =  this.PIN_STATUS.REQUESTED;
        if(this.approveStatus)
            this.approve(this.approvalMessage);
        else{
            this.reject(this.approvalMessage);
        }
    }

    onSharesMenuClick(shareMenutItem: VS.VersionShareListItemMenuClick) {
        switch(shareMenutItem.key) {
            case "archive-share":
                // this.setSharesMenu();
                this.archive(shareMenutItem.share,shareMenutItem.menuIdentifier );

                break;
            case "reset-share":
                // this.setSharesMenu();
                this.reset(shareMenutItem.share,shareMenutItem.menuIdentifier);
                break;
        }
    }

    public dismiss(e:any){
        this.view = this.VIEW_APPROVAL_NONE;
        this.pinRequestStatus =  this.PIN_STATUS.DEFAULT;
    }

    public dismissArchiveAction(e:any){
        this.versionsViewSrv.resetArchiveActionStatus();
        this.view = this.VIEW_APPROVAL_NONE;
    }

    public dismissResetAction(e:any){
        this.versionsViewSrv.resetResetActionStatus();
        this.view = this.VIEW_APPROVAL_NONE;
    }

    public retry(e:any){
        switch(this.view) {
            case this.VIEW_APPROVAL_SIGNATURE_ERROR:
                if (this.approveStatus)
                    this.view = this.VIEW_APPROVAL_APPROVE_MODAL;
                else
                    this.view = this.VIEW_APPROVAL_REJECT_MODAL;
                break;
            case this.VIEW_FEEDBACK_ACTION_ERROR:
                this.status = this.VIEW_APPROVAL_NONE;
                break;
            default:
                this.status = this.VIEW_APPROVAL_NONE;
                break;
        }
    }

    public onFeedbackSent(e){
        this.view = this.VIEW_APPROVAL_NONE;
    }

    /**
     * This calls for approve/reject and manage sign modals when a signature type is returned
     */
    private approvalFeedbackAction() : void {
        this.versionsSrv.giveFeedback$(this.version,this.userApproval.entry.token, this.approvalMessage, this.approveStatus).subscribe(
            (data:ApprovalActionResponse) => {
                if( this.userApproval != null)
                    this.feedbackDone.emit(this.userApproval);
                console.debug("Approved sent "+this.version.id,data);
                // this.loadMyFeedback(this.version);
                this.view = this.VIEW_APPROVAL_DONE;
                this.approvalMessage = '';
                this.approvalActionDisabled = false;
            },
            (error:any) => { 
                if(error.status == ApprovalActionResponseCodeEnum.MORE_DATA ){ //422
                    this.view = this.VIEW_APPROVAL_APPROVE_SIGNATURE;

                    if(this.pinRequestStatus == this.PIN_STATUS.REQUESTED){
                        this.pinRequestStatus =  this.PIN_STATUS.RENEWED
                    }
                    
                    if(error?.error){
                        this.signatureType = error.error.signature;
                    }
                } else if(error.status == ApprovalActionResponseCodeEnum.NO_PASSWORD){ //412
                    
                    this.view = this.VIEW_APPROVAL_SET_PASS_MODAL;
                } else if(error.status == ApprovalActionResponseCodeEnum.NOT_ACCEPTABLE){ //406
                    
                    this.view = this.VIEW_APPROVAL_ACTION_ERROR;
                }
                else { // regular or general error
                    if(this.pinRequestStatus == this.PIN_STATUS.REQUESTED){
                        this.pinRequestStatus =  this.PIN_STATUS.ERROR; // not used now
                    }
                    this.view = this.VIEW_FEEDBACK_ACTION_ERROR;
                }
                this.approvalActionDisabled = false;
            }
        );
    }

    private archive(share: VS.VersionShare, menuId: string){
        this.view = this.VIEW_APPROVAL_NONE;
        if(share != null){
            // console.log(" The feedback to archive is ",feedback);
            // action in service and store, with status of the call observed with a selector
            this.versionsViewSrv.archiveVersionShare(share, menuId);

            this.versionShareStatusUpdated.emit(share);
        }
    }

    private reset(share: VS.VersionShare, menuId: string){
        this.view = this.VIEW_APPROVAL_NONE;
        if(share != null){
            // action in service and store, with status of the call observed with a selector
            this.versionsViewSrv.resetVersionShare(share, menuId);

            this.versionShareStatusUpdated.emit(share);
        }
    }

    private setSharesMenu(){
        if(this.accessFeedbacks && this.accessProject){
            // fix 16/07/2024 <diego@twonas.com> archive menu is there by default! for validated accounts
            this.updateSharesMenuItems(this.archiveShareMenu)
            this.updateSharesMenuItems(this.resetShareMenu,this.accessProject[AccessProjectKeys.UPDATE_SHARE_STATUS]);
        }
    }

    // this function check either remove or add item. based on subscribed access
    private updateSharesMenuItems(item: ThreeDotsMenuItem, addItem:boolean = true) {
        this.sharesMenu = this.sharesMenu.filter((i: ThreeDotsMenuItem) => item.key != i.key);
        if(addItem){
            this.sharesMenu.push(item);
        }
    }

}
